import React, { useState, useEffect } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Diamond, Zap, Crown, Star, Check, Sparkles } from 'lucide-react';

const PACK_ICONS = { starter: Zap, standard: Star, premium: Diamond, mega: Crown };
const PACK_COLORS = { starter: 'from-blue-500 to-cyan-500', standard: 'from-violet-500 to-purple-500', premium: 'from-brand-pink to-rose-500', mega: 'from-amber-400 to-yellow-500' };
const SUB_ICONS = { basic: Star, premium: Diamond, gold: Crown };
const SUB_COLORS = { basic: 'from-blue-500 to-cyan-500', premium: 'from-brand-pink to-rose-500', gold: 'from-amber-400 to-yellow-500' };

export default function Store() {
  const { user } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('credits');
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    api.get('/pricing').then((r) => setPricing(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleBuyCredits = async (packName) => {
    setPurchasing(packName);
    try {
      const res = await api.post('/credits/purchase-session', { pack_name: packName });
      if (res.data?.url) window.open(res.data.url, '_blank');
    } catch (e) { console.error('Purchase error:', e); }
    setPurchasing(null);
  };

  const handleSubscribe = async (tier) => {
    setPurchasing(tier);
    try {
      const res = await api.post('/subscriptions/checkout', { tier, period: 'monthly' });
      if (res.data?.url) window.open(res.data.url, '_blank');
    } catch (e) { console.error('Subscribe error:', e); }
    setPurchasing(null);
  };

  return (
    <div className="h-full overflow-y-auto" data-testid="store-page">
      <header className="px-8 py-6" style={{borderBottom:'1px solid var(--border-color)'}}>
        <h1 className="text-2xl font-black" style={{color:'var(--text-primary)'}}>Boutique</h1>
        <p className="text-sm mt-1" style={{color:'var(--text-muted)'}}>Credits et abonnements</p>
      </header>

      <div className="max-w-5xl mx-auto p-8">
        <div className="glass-card p-6 mb-8 flex items-center justify-between">
          <div>
            <p className="text-sm" style={{color:'var(--text-muted)'}}>Votre solde</p>
            <p className="text-3xl font-black gradient-text mt-1">{user?.credits || 0} credits</p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{background:'var(--bg-input)', border:'1px solid var(--border-color)'}}>
            <Sparkles size={16} className="text-brand-violet" />
            <span className="text-sm font-medium capitalize" style={{color:'var(--text-secondary)'}}>{user?.subscription_tier || 'free'}</span>
          </div>
        </div>

        <div className="flex gap-2 mb-8">
          {[
            { key: 'credits', label: 'Credits', icon: Diamond },
            { key: 'subscriptions', label: 'Abonnements', icon: Crown },
          ].map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all ${
                tab === key ? 'bg-gradient-to-r from-brand-purple to-brand-violet text-white' : ''
              }`}
              style={tab !== key ? {background:'var(--bg-card)', color:'var(--text-muted)', border:'1px solid var(--border-color)'} : undefined}
              data-testid={`tab-${key}`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-brand-violet border-t-transparent rounded-full animate-spin" /></div>
        ) : tab === 'credits' ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {pricing?.credit_packs && Object.entries(pricing.credit_packs).map(([key, pack]) => {
                const Icon = PACK_ICONS[key] || Star;
                const gradient = PACK_COLORS[key] || 'from-gray-500 to-gray-600';
                const isPopular = key === 'premium';
                return (
                  <div key={key} className={`glass-card relative overflow-hidden ${isPopular ? 'ring-2 ring-brand-pink/50' : ''}`}>
                    {isPopular && (
                      <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-brand-pink to-brand-violet py-1 text-center">
                        <span className="text-[10px] font-bold text-white uppercase tracking-wider">Populaire</span>
                      </div>
                    )}
                    <div className={`p-6 ${isPopular ? 'pt-9' : ''}`}>
                      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-4`}>
                        <Icon size={22} className="text-white" />
                      </div>
                      <h3 className="text-lg font-bold capitalize mb-1" style={{color:'var(--text-primary)'}}>{key}</h3>
                      <p className="text-3xl font-black mb-1" style={{color:'var(--text-primary)'}}>{pack.credits} <span className="text-sm font-medium" style={{color:'var(--text-muted)'}}>credits</span></p>
                      <p className="text-xl font-bold gradient-text mb-4">{pack.price}€</p>
                      <button onClick={() => handleBuyCredits(key)} disabled={purchasing === key}
                        className={`w-full py-3 rounded-xl font-bold text-sm transition-all ${
                          isPopular ? 'bg-gradient-to-r from-brand-pink to-brand-violet text-white hover:shadow-lg hover:shadow-brand-pink/30' : ''
                        } disabled:opacity-50`}
                        style={!isPopular ? {background:'var(--bg-input)', color:'var(--text-primary)'} : undefined}
                        data-testid={`buy-${key}`}
                      >
                        {purchasing === key ? 'Chargement...' : 'Acheter'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {pricing?.feature_costs && (
              <div className="mt-10">
                <h3 className="text-lg font-bold mb-4" style={{color:'var(--text-primary)'}}>Cout des fonctionnalites</h3>
                <div className="glass-card p-6">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(pricing.feature_costs).map(([key, cost]) => (
                      <div key={key} className="flex items-center justify-between p-3 rounded-xl" style={{background:'var(--bg-card)'}}>
                        <span className="text-sm capitalize" style={{color:'var(--text-secondary)'}}>{key.replace(/_/g, ' ')}</span>
                        <span className="text-sm font-bold text-brand-violet">{cost} cr</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {pricing?.subscriptions && Object.entries(pricing.subscriptions).map(([key, sub]) => {
              const Icon = SUB_ICONS[key] || Star;
              const gradient = SUB_COLORS[key] || 'from-gray-500 to-gray-600';
              const isPopular = key === 'premium';
              return (
                <div key={key} className={`glass-card relative overflow-hidden ${isPopular ? 'ring-2 ring-brand-pink/50 scale-[1.02]' : ''}`}>
                  {isPopular && (
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-brand-pink to-brand-violet py-1.5 text-center">
                      <span className="text-xs font-bold text-white uppercase tracking-wider">Recommande</span>
                    </div>
                  )}
                  <div className={`p-7 ${isPopular ? 'pt-10' : ''}`}>
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-5`}>
                      <Icon size={26} className="text-white" />
                    </div>
                    <h3 className="text-xl font-black capitalize mb-1" style={{color:'var(--text-primary)'}}>{key}</h3>
                    <p className="text-sm mb-4" style={{color:'var(--text-muted)'}}>{sub.name || key}</p>

                    <div className="space-y-2 mb-6">
                      {sub.price_monthly && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm" style={{color:'var(--text-muted)'}}>Mensuel</span>
                          <span className="text-lg font-bold" style={{color:'var(--text-primary)'}}>{sub.price_monthly}€<span className="text-xs" style={{color:'var(--text-faint)'}}>/mois</span></span>
                        </div>
                      )}
                    </div>

                    {sub.features && (
                      <ul className="space-y-2 mb-6">
                        {sub.features.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm" style={{color:'var(--text-secondary)'}}>
                            <Check size={14} className="text-green-400 shrink-0 mt-0.5" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <button onClick={() => handleSubscribe(key)} disabled={purchasing === key}
                      className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all ${
                        isPopular ? 'bg-gradient-to-r from-brand-pink to-brand-violet text-white hover:shadow-lg hover:shadow-brand-pink/30' : ''
                      } disabled:opacity-50`}
                      style={!isPopular ? {background:'var(--bg-input)', color:'var(--text-primary)'} : undefined}
                      data-testid={`subscribe-${key}`}
                    >
                      {purchasing === key ? 'Chargement...' : "S'abonner"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
